import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Reorder } from 'framer-motion';
import { DraggableTab } from './DraggableTab';
import { HydratedPost, UserData, PostType, Post } from '../types';
import { useAuth, useAudioPlayerState } from '../contexts/AuthContext';
import { ProfileHeader } from './ProfileHeader';
import { PostGrid } from './PostGrid';
import { stickerTextStyle } from './StickerText';
import ProxiedIframe from './ProxiedIframe';

interface ProfilePageProps {
    posts: HydratedPost[];
    allPosts: HydratedPost[];
    onPostClick: (post: HydratedPost, contextList?: HydratedPost[]) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number) => void;
    onNavigateToProfile: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    tabsOrder?: string[];
    onReorderTabs?: (newOrder: string[]) => void;
    onCreatePost: (postData: {
        type: PostType,
        content: Post['content'],
        visibility?: 'public' | 'followers' | 'groups' | 'private',
        visibilityGroups?: string[],
        showCaption?: boolean,
        showOnProfile?: boolean,
        textSize?: 'xs' | 'sm' | 'med' | 'lg',
        communityId?: string,
        themeOpacity?: number,
        themeColor?: string,
        textColor?: string,
        isOptimistic?: boolean,
        localId?: number,
        fontId?: string,
        postcardStyle?: string,
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => void;
    isOwner: boolean;
    viewedUser: { id: string, data: UserData };
    onProfilePictureChange: (url: string) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    onProfileBgChange: (bg: { url: string, style: string }) => void;
    galleryAnimation: string;
    postBlurOpacity: number;
    onStartConversation: (userId: string) => void;
    onFollowToggle: (userId: string) => void;
    onShowFollowList: (listType: 'followers' | 'following' | 'friends' | 'connections', userId: string) => void;
    onVouch?: (postId: number) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onRemoveFollower?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    gridColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    onGridColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    onScaleChange?: (scale: number) => void;
    isModalOpen?: boolean;
    isDemo?: boolean;
    currentUser: string | null;
    onVisibleCountChange?: (count: number) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    initialVisibleCount?: number;
    isLoading?: boolean;
    onAddToCart?: (post: Post) => void;
    initialSharedContent?: { text: string, media: any[], sourcePostId?: number } | null;
    clearSharedContent?: () => void;
    onNavigateToYipYap?: (config: { name: string, mode: 'broadcast' | 'anonymous' | 'forum', visibility: 'public' | 'open', groupIds?: string[] }) => void;
    existingNonAnonymousYipYapNames?: string[];
    hideContent?: boolean;
    onPin?: (postId: number) => void;
    onUnpin?: (postId: number) => void;
    onReorderPinnedPosts?: (newPinnedOrder: number[]) => void;
    onRetryPostUpload?: (localId: number, startUpload: any) => void;
    postcardGap?: number;
    onPostDragProgress?: (post: any, progress: number, direction: 'left' | 'right' | null) => void;
}

export const ProfilePage = ({
    posts,
    allPosts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onShare,
    tabsOrder,
    onReorderTabs,
    onCreatePost,
    isOwner,
    viewedUser,
    onProfilePictureChange,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    onProfileBgChange,
    galleryAnimation,
    postBlurOpacity,
    onStartConversation,
    onFollowToggle,
    onShowFollowList,
    onVouch,
    onGrantAdmin,
    onDeleteUser,
    onMuteUser,
    onBlockUser,
    onRemoveFollower,
    onReportPost,
    gridColumns = 1,
    onGridColumnsChange,
    preferredScale = 1,
    onScaleChange,
    onSwipeTransition,
    isModalOpen = false,
    currentUser,
    initialVisibleCount = 24,
    onVisibleCountChange,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    isLoading,
    initialSharedContent,
    clearSharedContent,
    onNavigateToYipYap,
    hideContent = false,
    existingNonAnonymousYipYapNames,
    onPin,
    onUnpin,
    onReorderPinnedPosts,
    onRetryPostUpload,
    onAddToCart,
    postcardGap = 8,
    onPostDragProgress
}: ProfilePageProps) => {
    const { playAudioPost } = useAudioPlayerState();
    const [activeTabState, setActiveTabState] = useState<string | null>(null);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [headerModalOpen, setHeaderModalOpen] = useState(false);
    const [showPassionOverlay, setShowPassionOverlay] = useState(false);

    const autoplayedUserRef = useRef<string | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [viewedUser.id]);

    useEffect(() => {
        if (viewedUser.id && autoplayedUserRef.current !== viewedUser.id) {
            if (viewedUser.data?.playlist && viewedUser.data.playlist.length > 0 && playAudioPost) {
                autoplayedUserRef.current = viewedUser.id;
                const tracks = viewedUser.data.playlist.map((track: any) => ({
                    id: track.postId || `playlist-${viewedUser.id}-${track.id}`,
                    author: viewedUser.id,
                    type: 'audio',
                    content: {
                        text: track.title,
                        media: [{ url: track.url, type: 'audio' }]
                    },
                    timestamp: new Date().toISOString()
                }));
                const firstTrack = tracks[0];
                playAudioPost(firstTrack, tracks);
            }
        }
    }, [viewedUser.id, viewedUser.data?.playlist, playAudioPost]);

    const isFollowing = useMemo(() => {
        if (!currentUser || !viewedUser.id) return false;
        return viewedUser.data.followers?.includes(currentUser) || false;
    }, [currentUser, viewedUser.id, viewedUser.data.followers]);

    const canSeeComments = useMemo(() => {
        if (isOwner) return true;
        const visibility = viewedUser.data.commentsTabVisibility || 'public';
        if (visibility === 'public') return true;
        if (visibility === 'followers') return isFollowing;
        return false;
    }, [isOwner, viewedUser.data.commentsTabVisibility, currentUser, isFollowing]);

    const profileTabs = useMemo(() => {
        let baseTabs = isOwner
            ? ['All posts', 'OC', 'Bumps', 'Footprints', 'Media', 'Audio', 'Links', 'Yaps', 'Faves', 'Saves', 'Archive']
            : ['All posts', 'OC', 'Bumps', 'Footprints', 'Media', 'Audio', 'Links', 'Yaps'];

        if (viewedUser.data?.isVendor) {
            baseTabs = ['For Sale', ...baseTabs];
        }



        if (!tabsOrder || tabsOrder.length === 0) return baseTabs;

        const orderedTabs: string[] = [];
        const baseTabsSet = new Set(baseTabs);

        tabsOrder.forEach(id => {
            if (baseTabsSet.has(id)) {
                orderedTabs.push(id);
                baseTabsSet.delete(id);
            }
        });

        baseTabsSet.forEach(t => orderedTabs.push(t));
        return orderedTabs;
    }, [isOwner, canSeeComments, tabsOrder]);

    const activeTab = activeTabState || profileTabs[0] || 'All posts';
    const setActiveTab = setActiveTabState;

    useEffect(() => {
        onInternalModalStateChange?.(headerModalOpen);
    }, [headerModalOpen, onInternalModalStateChange]);

    const userPosts = useMemo(() => {
        return posts.filter(p => p.author === viewedUser.id);
    }, [posts, viewedUser.id]);

    const filteredPosts = useMemo(() => {
        if (activeTab === 'Archive') {
            return isOwner ? userPosts.filter(p => p.isArchived) : [];
        }

        const pinSortedUserPosts = (() => {
            const rawUser = userPosts.filter(p => !p.isArchived && (isOwner || p.showOnProfile !== false));
            const pinnedIds = viewedUser.data.pinnedPosts || [];

            const pinned = rawUser.filter(p => pinnedIds.includes(p.id));
            pinned.sort((a, b) => pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id));

            const unpinned = rawUser.filter(p => !pinnedIds.includes(p.id));
            return [...pinned, ...unpinned];
        })();

        const unarchivedAllPosts = allPosts.filter(p => !p.isArchived);

        switch (activeTab) {
            case 'For Sale':
                return pinSortedUserPosts.filter(p => p.isForSale);
            case 'OC':
                return pinSortedUserPosts.filter(p => p.type !== 'bump');
            case 'Bumps':
                return pinSortedUserPosts.filter(p => p.type === 'bump');
            case 'Footprints':
                return pinSortedUserPosts.filter(p => !!p.footprintDetails);
            case 'Media':
                return pinSortedUserPosts.filter(p => p.type === 'image' || p.type === 'video');
            case 'Audio':
                return pinSortedUserPosts.filter(p => p.type === 'audio' || p.type === 'voice');
            case 'Links':
                return pinSortedUserPosts.filter(p => p.type === 'link');
            case 'Yaps': {
                const ownTextPosts = pinSortedUserPosts.filter(p => p.type === 'text');
                const commentedPosts = canSeeComments
                    ? unarchivedAllPosts.filter(p => p.comments.some(c => c.author === viewedUser.id))
                    : [];
                
                const combined = [...ownTextPosts];
                commentedPosts.forEach(cp => {
                    if (!combined.some(p => p.id === cp.id)) {
                        combined.push(cp);
                    }
                });

                combined.sort((a, b) => {
                    const pinnedIds = viewedUser.data.pinnedPosts || [];
                    const aPinned = pinnedIds.includes(a.id) && a.author === viewedUser.id;
                    const bPinned = pinnedIds.includes(b.id) && b.author === viewedUser.id;
                    if (aPinned && !bPinned) return -1;
                    if (!aPinned && bPinned) return 1;
                    if (aPinned && bPinned) return pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id);
                    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
                });

                return combined;
            }
            case 'Faves':
                return isOwner ? unarchivedAllPosts.filter(p => viewedUser.data.likedPosts?.includes(p.id)) : [];
            case 'Saves':
                return isOwner ? unarchivedAllPosts.filter(p => viewedUser.data.bookmarkedPosts?.includes(p.id)) : [];
            case 'All posts':
            default:
                return pinSortedUserPosts;
        }
    }, [activeTab, posts, userPosts, allPosts, isOwner, viewedUser.data]);

    const headerTextColor = viewedUser.data.appTextColor || '#ffffff';
    const headerFontFamily = viewedUser.data.appFontFamily || undefined;

    return (
        <div className="relative">
            {viewedUser.data.passion && showPassionOverlay && (
                <div className="fixed inset-0 z-[9999] flex flex-col bg-[#0d1117] text-white">
                    <div className="flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+var(--safe-area-top,0px))] border-b border-white/10 bg-[#161b22]" style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowPassionOverlay(false)}
                                className="relative w-10 h-10 rounded-full border border-white/20 active:scale-95 transition-all overflow-hidden flex-shrink-0"
                                title="Back to profile"
                            >
                                <img src={viewedUser.data.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(viewedUser.id) + '&backgroundColor=b6e3f4'} alt="Back" className="w-full h-full object-cover" />
                            </button>
                            <div>
                                <div className="text-xs font-bold truncate max-w-[180px]">{viewedUser.data.displayName}</div>
                                <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{viewedUser.data.passion}</div>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowPassionOverlay(false)}
                            className="px-3 py-1 text-xs font-semibold bg-white/10 rounded-lg active:scale-95 transition-all hover:bg-white/20"
                        >
                            Done
                        </button>
                    </div>

                    <div className="flex-1 w-full overflow-hidden bg-black">
                        <ProxiedIframe
                            url={viewedUser.data.passion}
                            className="w-full h-full border-none"
                            style={{ height: '100%', width: '100%' }}
                        />
                    </div>
                </div>
            )}

            <ProfileHeader
                userData={viewedUser.data}
                userId={viewedUser.id}
                isOwner={isOwner}
                onProfilePictureChange={onProfilePictureChange}
                onProfileBgChange={onProfileBgChange}
                onStartConversation={onStartConversation}
                postCount={userPosts.length}
                onFollowToggle={onFollowToggle}
                onGrantAdmin={onGrantAdmin}
                onDeleteUser={onDeleteUser}
                onMuteUser={onMuteUser}
                onBlockUser={onBlockUser}
                onRemoveFollower={onRemoveFollower}
                onShowFollowers={() => onShowFollowList('friends', viewedUser.id)}
                onShowFollowing={() => onShowFollowList('following', viewedUser.id)}
                onShowConnections={() => onShowFollowList('connections', viewedUser.id)}
                onShare={onShare}
                canSeeComments={canSeeComments}
                onInternalModalStateChange={setHeaderModalOpen}
                closeInternalModalsTrigger={closeInternalModalsTrigger}
                onNavigateToYipYap={onNavigateToYipYap}
                existingNonAnonymousYipYapNames={existingNonAnonymousYipYapNames}
                onNavigateToProfile={onNavigateToProfile}
                onNavigateToHashtag={onNavigateToHashtag}
                showPassionOverlay={showPassionOverlay}
                onPfpClick={() => viewedUser.data.passion && setShowPassionOverlay(!showPassionOverlay)}
            />

            {!showPassionOverlay && (
                <div className={`transition-opacity duration-500 ${hideContent ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                    {isReorderMode && (
                        <div 
                            className="fixed inset-0 z-40 bg-transparent" 
                            onClick={() => setIsReorderMode(false)}
                            onTouchStart={() => setIsReorderMode(false)}
                        />
                    )}
                    <div className={`w-full px-4 lg:px-[5%] mb-4 ${isReorderMode ? 'relative z-50' : ''}`}>
                        <nav
                            className="flex space-x-6 overflow-x-auto no-scrollbar py-1"
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{ touchAction: isReorderMode ? 'none' : 'pan-x', fontFamily: headerFontFamily }}
                        >
                            <Reorder.Group 
                                axis="x" 
                                values={profileTabs} 
                                onReorder={(newOrder) => onReorderTabs && onReorderTabs(newOrder)} 
                                className="flex space-x-6 h-full"
                                style={{ overflow: 'visible' }}
                            >
                                {profileTabs.map(tab => (
                                    <DraggableTab
                                        key={tab}
                                        item={tab}
                                        label={tab}
                                        isActive={activeTab === tab}
                                        onClick={() => setActiveTab(tab)}
                                        color={headerTextColor}
                                        isReorderMode={isReorderMode}
                                        onEnterReorderMode={() => setIsReorderMode(true)}
                                        onExitReorderMode={() => setIsReorderMode(false)}
                                    />
                                ))}
                            </Reorder.Group>
                        </nav>
                    </div>
                    <PostGrid key={activeTab} posts={filteredPosts} onPostClick={(p) => onPostClick(p, filteredPosts)} onLike={onLike} onBump={onBump} onBookmark={onBookmark} onEdit={onEdit} onDelete={onDelete} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} onNavigateToCommunity={onNavigateToCommunity} onShare={onShare} onVouch={onVouch} onMuteUser={onMuteUser} onBlockUser={onBlockUser} onGrantAdmin={onGrantAdmin} onDeleteUser={onDeleteUser} onReportPost={onReportPost} animatingLikes={animatingLikes} animatingBumps={animatingBumps} animatingBookmarks={animatingBookmarks} galleryAnimation={galleryAnimation} postBlurOpacity={postBlurOpacity} preferredColumns={gridColumns} onColumnsChange={onGridColumnsChange} preferredScale={preferredScale} onScaleChange={onScaleChange}
                        onSwipeTransition={onSwipeTransition}
                        isModalOpen={isModalOpen}
                        initialVisibleCount={initialVisibleCount}
                        onVisibleCountChange={onVisibleCountChange}
                        isLoading={isLoading}
                        onPin={onPin}
                        onUnpin={onUnpin}
                        onReorderPinnedPosts={onReorderPinnedPosts}
                        isProfile={true}
                        onRetryPostUpload={onRetryPostUpload}
                        onAddToCart={onAddToCart}
                        postcardGap={postcardGap}
                        onPostDragProgress={onPostDragProgress}
                    />
                </div>
            )}
        </div>
    )
};
